import tokenEvents, { TOKEN_EVENTS } from './tokenEvents';
import appInsightsService from '@/utils/appInsights';

/**
 * TokenRefreshManager - Singleton class that manages token refresh logic
 * Coordinates between app and Service Worker for automatic token renewal
 */
class TokenRefreshManager {
  constructor() {
    this.worker = null;

    // Map<sessionId, callback> — supports AUTOMY, PORTAL, and APP sessions concurrently.
    // Replaces the single this.currentSessionId + this.refreshCallback pair that caused
    // the last caller to overwrite all prior sessions in multi-session scenarios.
    this.refreshCallbacks = new Map();
    this.refreshTimers = new Map();
    this.isInitialized = false;

    // Per-session refresh guard — replaces the single isRefreshing boolean that
    // blocked all sessions when any one session was mid-refresh.
    this.refreshingSessions = new Set();

    // Guard so the visibilitychange listener is only attached once, regardless
    // of how many init paths run (SW success / no SW / registration failure).
    this._visibilityListenerAttached = false;

    // Kept for backward-compat: callers that use setRefreshCallback() without passing
    // a callback to startTokenRefresh() get this as the per-session default.
    this._pendingCallback = null;
  }

  /**
   * Initializes the token refresh manager and registers Service Worker
   * @returns {Promise<boolean>} Success status
   */
  async init() {
    if (this.worker) return true;
    // Attach the visibility recheck regardless of Service Worker availability —
    // the fallback-timer path (no SW) also needs to re-evaluate expiry on wake.
    this._ensureVisibilityListener();
    if (!navigator.serviceWorker) {
      appInsightsService.trackException(
        new Error('Service Workers not supported'),
        {
          errorType: 'WORKER_NOT_SUPPORTED',
          browser: navigator.userAgent,
          isServiceWorkerSupported: false,
          errorReason: 'Service Workers not available in this browser',
          fallbackUsed: true,
        },
        2, // Warning severity - expected in some browsers
      );

      this.isInitialized = true;
      return true;
    }

    try {
      const workerURL = '/tokenRefreshWorker.js';
      const registration = await navigator.serviceWorker.register(workerURL, { scope: '/' });

      if (registration.active) {
        this.worker = registration.active;
      } else {
        // Wait for worker to activate with timeout
        const activationTimeout = 10000; // 10 seconds
        const activationPromise = new Promise((resolve, reject) => {
          const worker = registration.installing || registration.waiting;
          if (!worker) {
            reject(new Error('No worker available for activation'));
            return;
          }

          const timeoutId = setTimeout(() => {
            reject(new Error('Worker activation timeout'));
          }, activationTimeout);

          worker.addEventListener('statechange', () => {
            if (worker.state === 'activated') {
              clearTimeout(timeoutId);
              this.worker = worker;
              resolve();
            } else if (worker.state === 'redundant') {
              clearTimeout(timeoutId);
              reject(new Error('Worker became redundant during activation'));
            }
          });
        });

        await activationPromise;
      }

      // Setup message handler
      navigator.serviceWorker.addEventListener('message', this.handleWorkerMessage.bind(this));
      this._ensureVisibilityListener();
      this.isInitialized = true;
      return true;
    } catch (error) {
      const isRegistrationError =
        error.message?.includes('register') || error.name === 'SecurityError';

      const isActivationError =
        error.message?.includes('activation') || error.message?.includes('timeout');

      appInsightsService.trackException(
        error,
        {
          errorType: 'WORKER_REGISTRATION_FAILED',
          browser: navigator.userAgent,
          isServiceWorkerSupported: !!navigator.serviceWorker,
          errorReason: error.message || 'Unknown registration error',
          errorName: error.name,
          workerState: this.worker?.state || 'unknown',
          isRegistrationError,
          isActivationError,
          isRedundantError: error.message?.includes('redundant'),
          securityContext:
            error.name === 'SecurityError' ? 'Possible HTTPS or permissions issue' : undefined,
          fallbackUsed: true,
          workerURL: '/tokenRefreshWorker.js',
        },
        3,
      );

      this.isInitialized = true;
      return true;
    }
  }

  /**
   * On page visibility restore (e.g. laptop wake), tells the SW to re-evaluate
   * all session timers. Catches the case where a timer fired while the tab was
   * suspended and the REFRESH_TOKEN_REQUEST was never processed.
   */
  /**
   * Attaches the visibilitychange listener exactly once. Safe to call from any
   * init path (SW active, SW unsupported, or registration failed).
   */
  _ensureVisibilityListener() {
    if (this._visibilityListenerAttached) return;
    if (typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
    this._visibilityListenerAttached = true;
  }

  _handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;

    if (this.worker) {
      this.worker.postMessage({ type: 'RECHECK', payload: {} });
      return;
    }

    // No Service Worker: fallback setTimeout timers are unreliable after the
    // device suspends (they can fire late or not at all). Re-evaluate every
    // monitored session on wake. executeTokenRefresh delegates to the per-session
    // callback, which self-guards and only refreshes when actually near expiry.
    this.refreshCallbacks.forEach((_cb, sessionId) => {
      this.executeTokenRefresh(sessionId);
    });
  };

  /**
   * Handles messages from Service Worker.
   * Looks up the callback by sessionId from the message payload so all active
   * sessions (AUTOMY, PORTAL, APP) are handled independently.
   */
  handleWorkerMessage = (event) => {
    const { type, payload } = event.data;
    if (type === 'REFRESH_TOKEN_REQUEST') {
      const { sessionId } = payload;
      if (this.refreshCallbacks.has(sessionId)) {
        this.executeTokenRefresh(sessionId);
      }
    }
  };

  /**
   * Executes token refresh and emits global events
   * @param {string} sessionId - Session identifier
   */
  async executeTokenRefresh(sessionId) {
    if (this.refreshingSessions.has(sessionId)) return;

    const run = async () => {
      if (this.refreshingSessions.has(sessionId)) return;
      this.refreshingSessions.add(sessionId);
      try {
        const callback = this.refreshCallbacks.get(sessionId);
        if (callback) await callback(sessionId);
      } catch (error) {
        tokenEvents.emit(TOKEN_EVENTS.TOKEN_REFRESH_FAILED, { sessionId, error });
      } finally {
        this.refreshingSessions.delete(sessionId);
      }
    };

    // navigator.locks serializes refresh across all tabs for the same session.
    // When Tab A holds the lock, Tab B waits — preventing the multi-tab TOCTOU
    // race and the proactive/reactive dual-path race.
    // IE11 fallback: lock unavailable, falls through to in-process guard only.
    if (typeof navigator !== 'undefined' && navigator.locks) {
      await navigator.locks.request(`token-refresh-${sessionId}`, run);
    } else await run();
  }

  /**
   * Sets a default callback used by the next startTokenRefresh call.
   * Preserved for callers (useTokenRefresh.js) that set the callback separately.
   * @param {Function} callback - Function to call for token refresh
   */
  setRefreshCallback(callback) {
    this._pendingCallback = callback;
  }

  /**
   * Starts monitoring token expiration for a session.
   * Stores the callback in the per-session Map so concurrent AUTOMY / PORTAL / APP
   * sessions don't overwrite each other.
   * @param {string} sessionId - Session identifier
   * @param {string} token - Access token
   * @param {number} expiresAt - Token expiration timestamp
   * @param {Function} [callback] - Optional per-call callback; falls back to _pendingCallback
   * @returns {Promise<boolean>} Success status
   */
  async startTokenRefresh(sessionId, token, expiresAt, callback) {
    // Associate the callback with this specific session.
    // Prefer an explicitly-passed callback; fall back to the one set via setRefreshCallback().
    const resolvedCallback = callback || this._pendingCallback;
    if (resolvedCallback) this.refreshCallbacks.set(sessionId, resolvedCallback);
    if (this.worker) {
      try {
        this.worker.postMessage({
          type: 'START_REFRESH',
          payload: { sessionId, token, expiresAt },
        });
        return true;
      } catch (error) {
        appInsightsService.trackException(
          error,
          {
            messageType: 'START_REFRESH',
            workerState: this.worker?.state || 'unknown',
            errorContext: 'Service Worker communication failure',
          },
          2,
        );
      }
    }

    this.startFallbackTimer(sessionId, expiresAt);
    return true;
  }

  /**
   * Fallback timer implementation when Service Worker is unavailable.
   * Uses a per-session Map so concurrent sessions each get their own timer.
   * @param {string} sessionId - Session identifier
   * @param {number} expiresAt - Token expiration timestamp
   */
  startFallbackTimer(sessionId, expiresAt) {
    const existing = this.refreshTimers.get(sessionId);
    if (existing) clearTimeout(existing);

    const timeUntilRefresh = expiresAt - Date.now() - 5 * 60 * 1000;

    if (timeUntilRefresh <= 0) {
      this.executeTokenRefresh(sessionId);
    } else {
      const timer = setTimeout(() => {
        this.executeTokenRefresh(sessionId);
      }, timeUntilRefresh);
      this.refreshTimers.set(sessionId, timer);
    }
  }

  /**
   * Updates token information after successful refresh.
   * No longer guards on a single currentSessionId — any active session is valid.
   * @param {string} sessionId - Session identifier
   * @param {string} token - New access token
   * @param {number} expiresAt - New expiration timestamp
   */
  async updateToken(sessionId, token, expiresAt) {
    // Emit global event that token was refreshed
    tokenEvents.emit(TOKEN_EVENTS.TOKEN_REFRESHED, {
      sessionId,
      token,
      expiresAt,
      timestamp: Date.now(),
    });

    if (this.worker) {
      try {
        this.worker.postMessage({
          type: 'UPDATE_TOKEN',
          payload: { sessionId, token, expiresAt },
        });
      } catch (error) {
        appInsightsService.trackException(
          error,
          {
            messageType: 'UPDATE_TOKEN',
            workerState: this.worker?.state || 'unknown',
            errorContext: 'Service Worker token update failure',
          },
          2,
        );
        this.startFallbackTimer(sessionId, expiresAt);
      }
    } else this.startFallbackTimer(sessionId, expiresAt);
  }

  /**
   * Notifies that a socket reconnection is needed
   * @param {string} reason - Reason for reconnection
   */
  requestSocketReconnect(reason = 'token_refresh') {
    // Emit with all active session IDs so listeners can correlate correctly.
    const sessionIds = Array.from(this.refreshCallbacks.keys());
    tokenEvents.emit(TOKEN_EVENTS.SOCKET_RECONNECT_NEEDED, {
      reason,
      timestamp: Date.now(),
      sessionIds,
      // Keep singular sessionId for backward-compat listeners that expect it.
      sessionId: sessionIds[0] ?? null,
    });
  }

  /**
   * Stops token refresh monitoring for a specific session and cleans up its resources.
   * @param {string} sessionId - Session identifier to stop
   */
  async stopTokenRefresh(sessionId) {
    if (!sessionId) {
      // Legacy call-site in useTokenRefresh cleanup passes no argument — stop all sessions.
      const sessionIds = Array.from(this.refreshCallbacks.keys());
      await Promise.all(sessionIds.map((id) => this.stopTokenRefresh(id)));
      this._pendingCallback = null;
      this.refreshingSessions.clear();
      return;
    }

    this.refreshCallbacks.delete(sessionId);
    const timer = this.refreshTimers.get(sessionId);

    if (timer) {
      clearTimeout(timer);
      this.refreshTimers.delete(sessionId);
    }

    if (this.worker) {
      try {
        this.worker.postMessage({ type: 'STOP_REFRESH', payload: { sessionId } });
      } catch (error) {}
    }
  }
}

export default new TokenRefreshManager();
